import { useTranslation } from "react-i18next";
import { type WorkflowStepStatus, WorkflowStepStatuses } from "~/api/types";
import { Checkbox } from "~/atoms/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { formatDate, toDate } from "~/helpers/date";
import { isBeforeToday, type WorkflowDateKey } from "~/helpers/workflowDates";
import {
  type WorkflowDates,
  type WorkflowDatesEditor,
} from "~/hooks/useWorkflowDatesEditor";
import { cn } from "~/lib/utils";
import { BulkEditableCell } from "~/molecules/BulkEditableCell";
import DatePicker from "~/molecules/DatePicker";
import { StatusBadge } from "~/molecules/WorkflowStatusBadge";

interface Props {
  rows: { id: number; title: string }[];
  steps: { name: string; status?: WorkflowStepStatus }[];
  readOnly?: boolean;
  datesEditor: WorkflowDatesEditor;
}

interface DateColumn {
  field: WorkflowDateKey;
  label: string;
  status?: WorkflowStepStatus;
}

export const dueDateGridDialogClassName = "w-fit max-w-[90vw] sm:max-w-[90vw]";

const fieldRowClassName = "flex h-8 items-center";

const truncatedTitleClassName = "max-w-60 truncate";

const valueOf = (dates: WorkflowDates, field: WorkflowDateKey): Date | null =>
  typeof field === "number"
    ? (dates.stepDueDates[field] ?? null)
    : dates[field];

export function DueDateGrid({
  rows,
  steps,
  readOnly = false,
  datesEditor,
}: Props) {
  const { t } = useTranslation();

  const selectable = !readOnly && datesEditor.canBulkEdit;

  const columns: DateColumn[] = [
    { field: "reviewFrom", label: t(($) => $.commons.document.reviewFrom) },
    ...steps.map((step, index) => ({
      field: index,
      label: step.name,
      status: step.status,
    })),
    { field: "reviewTo", label: t(($) => $.commons.document.reviewTo) },
  ];

  const errorMessage = (rowId: number, field: WorkflowDateKey) => {
    if (typeof field === "number") {
      const issue = datesEditor.issues.get(rowId)?.get(field);
      if (!issue) return undefined;
      return issue === "afterReviewTo"
        ? t(($) => $.commons.workflow.responseDateAfterReviewToWarning)
        : t(($) => $.commons.workflow.responseDateWarning);
    }

    if (field === "reviewTo" && datesEditor.reversedWindows.has(rowId)) {
      return t(($) => $.commons.workflow.reviewToBeforeFromWarning);
    }

    return undefined;
  };

  const warningMessage = (value: Date | null, field: WorkflowDateKey) => {
    if (typeof field === "number" || !isBeforeToday(value)) return undefined;
    return field === "reviewFrom"
      ? t(($) => $.commons.workflow.reviewFromPastWarning)
      : t(($) => $.commons.workflow.reviewToPastWarning);
  };

  const renderPicker = (
    rowId: number,
    field: WorkflowDateKey,
    value: Date | null,
  ) => {
    const bounds = datesEditor.bounds.get(rowId)?.get(field);
    const error = errorMessage(rowId, field);

    return (
      <BulkEditableCell
        hasError={error !== undefined}
        highlighted={datesEditor.isHighlighted(rowId, field)}
        onHover={
          selectable ? () => datesEditor.onHover(rowId, field) : undefined
        }
        onHoverLeave={selectable ? datesEditor.onHoverLeave : undefined}
        errorMessage={error}
        warningMessage={warningMessage(value, field)}
      >
        <DatePicker
          value={value}
          onChange={(date) =>
            datesEditor.updateCell(rowId, field, date ?? null)
          }
          minDate={toDate(bounds?.minDueAt) ?? undefined}
          maxDate={toDate(bounds?.maxDueAt) ?? undefined}
        />
      </BulkEditableCell>
    );
  };

  return (
    <Table
      className="border-collapse"
      wrapperClassName="w-max max-w-[calc(90vw-2rem)] max-h-[60vh] rounded-md border"
    >
      <TableHeader className="bg-muted sticky top-0 z-10">
        <TableRow>
          {selectable && (
            <TableHead className="w-10 px-3 py-2">
              <Checkbox
                checked={datesEditor.allSelected}
                onCheckedChange={datesEditor.toggleAll}
                aria-label={t(($) => $.commons.buttons.selectAll)}
              />
            </TableHead>
          )}
          <TableHead className="min-w-50 px-3 py-2">
            {t(($) => $.workflowTemplates.applyDatesDialog.documentTitle)}
          </TableHead>
          {columns.map((column) => (
            <TableHead
              key={String(column.field)}
              className="min-w-45 px-3 py-2"
            >
              <div className={truncatedTitleClassName} title={column.label}>
                {column.label}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const dates = datesEditor.dates.get(row.id);
          if (!dates) return null;

          return (
            <TableRow key={row.id}>
              {selectable && (
                <TableCell className="px-3 py-2 align-top">
                  <div className={fieldRowClassName}>
                    <Checkbox
                      checked={datesEditor.selectedIds.has(row.id)}
                      onCheckedChange={() => datesEditor.toggleRow(row.id)}
                      aria-label={t(($) => $.commons.buttons.selectItem, {
                        name: row.title,
                      })}
                    />
                  </div>
                </TableCell>
              )}
              <TableCell className="px-3 py-2 align-top font-medium">
                <div className={fieldRowClassName}>
                  <span className={truncatedTitleClassName} title={row.title}>
                    {row.title}
                  </span>
                </div>
              </TableCell>
              {columns.map((column) => {
                const locked =
                  readOnly || datesEditor.isLocked(row.id, column.field);
                const value = valueOf(dates, column.field);

                return (
                  <TableCell
                    key={String(column.field)}
                    className={cn(
                      "px-3 py-2 align-top",
                      locked && "text-muted-foreground",
                    )}
                  >
                    {locked ? (
                      <div className={cn(fieldRowClassName, "gap-2")}>
                        {formatDate(value)}
                        {column.status === WorkflowStepStatuses.completed && (
                          <StatusBadge status={column.status} />
                        )}
                      </div>
                    ) : (
                      renderPicker(row.id, column.field, value)
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
